import PartnerLogo from "@components/Location/PartnerLogo";
import NextImage from "@components/NextImage";
import moment from "moment-timezone";
import Link from "next/link";
import {useTranslation} from "@i18n/client";
import React from "react";
import useLocationConstants, {
  LocationInfoBlurb,
  isFullPageBanner,
} from "src/constants/LocationConstants";
import {NEXT_PUBLIC_GOOGLE_API_KEY} from "src/publicEnv";

import {useFetchCareEntries} from "../../../queries/useFetchCareEntries";
import {Coordinate} from "../../../store/types";
import useMediaQuery from "../../../useMediaQuery";
import {s3ImageSource} from "../../../useS3ImgSrc";
import {analytics} from "../../../utils/analytics";
import {useGeolocation} from "../../../utils/browser-storage/location";
import {useFeaturedCareSlugs} from "../../../utils/getFeaturedCares";
import {getGoogleMapsDirections} from "../../../utils/getGoogleMapDirections";
import {googleStaticMapMarker, v5Pages} from "../../_common/_constants";
import {formatPhoneNumber, getAddress, groupHours, isLocationOpen} from "../../_common/Carbon";
import {CareEntryRowVariant} from "../CareEntryRow";
import {useSendDefaultResultsViewedEvent} from "../Reason/CareDiscoverySearch/careDiscoveryAnalytics";
import CareDiscoverySearch from "../Reason/CareDiscoverySearch/CareDiscoverySearch";
import {Btn} from "../V5Comp";
import Details from "./Details";
import ImageCarousel from "./ImageCarousel";
import {isBookingDisabled} from "./isBookingDisabled";
import styles from "./Main.module.scss";
import {useTypedSelector} from "../../../store";
import {roundedMilesBetweenCoordinates} from "../../../utils/distance";

export const getHoursText = ({name, from, to}: any) => {
  const fromStr = moment(from, "HH:mm").format("h:mm A");
  const toStr = moment(to, "HH:mm").format("h:mm A");

  return (
    <>
      <span className={styles.hours__left}>{name}</span>
      <span className={styles.hours__right}>
        {fromStr} – {toStr}
      </span>
    </>
  );
};

const reasonColors = [
  {bg: "#58B3CA", light: false},
  {bg: "#E15C18", light: false},
  {bg: "#19191B", light: false},
  {bg: "#A3E1C2", light: true},
  {bg: "#58B3CA", light: false},
  {bg: "#BAA3BF", light: false},
  {bg: "#EBC627", light: true},
];

const mapDimensions = [700, 400];

const Main = (props: {
  h1?: string;
  isPopup: boolean;
  popularReasons: any[];
  showImages?: boolean;
  location: any;
}) => {
  const {h1, location = {}, isPopup, popularReasons, showImages: showImagesParent} = props;
  const i18n = useTranslation();
  const geoLocation = useGeolocation();

  const {services, specialties = {}, name, address, phoneNumber, images} = location;

  const {banners, logo} = useLocationConstants({locationId: location.id});

  const isMd = useMediaQuery("md");

  const {locations} = useTypedSelector(state => state.config);
  const reasons = useTypedSelector(state => state.config.reasons) ?? [];

  const featuredCareSlugs = useFeaturedCareSlugs(locations, location.specialtyIds);

  const defaultResultsQuery = useFetchCareEntries(featuredCareSlugs);

  const noResults = defaultResultsQuery.isSettled && defaultResultsQuery.data.length === 0;
  const loadingRowCount = !defaultResultsQuery.isSettled ? featuredCareSlugs.length : undefined;

  const viewId = useSendDefaultResultsViewedEvent({
    results: defaultResultsQuery.data,
    isLoading: !defaultResultsQuery.isSettled,
    reasons,
    location,
  });

  const {latitude, longitude} = address;

  const directionsLink = getGoogleMapsDirections(address, location.googlePlaceId);

  const showImages = Array.isArray(images) && images.length > 0;

  const shouldWidgetStick = !isMd;

  const widgetTopMargin = shouldWidgetStick && showImagesParent ? -100 : 0;
  const isXs = useMediaQuery("xs");
  const imageWindowWidth = isXs ? "100vw" : "80vw";

  return (
    <div className="mobileContainer pt-4">
      <div className="pl26 pl0-md db-md dn">
        <Details h1={h1} location={location} logo={logo} />

        {showImages && (
          <ImageCarousel
            label={i18n.t("Location Photos")}
            imageWindowWidth={imageWindowWidth}
            images={images
              .map<string | Coordinate>(i => s3ImageSource(i.imageId))
              .insertAt(1, {x: address.latitude, y: address.longitude})}
            onFirstImageScroll={() => {
              analytics.post({
                category: "LocationPageInfo",
                label: "ClinicPhoto",
                action: "Scrolled",
              });
            }}
          />
        )}
      </div>
      <div
        id="locationWidgetWrapper"
        className={`flex py-4 lg:px-4 ${isMd ? "flex-col-reverse" : "gap-8 max-w-[85rem] m-auto"}`}
      >
        <div className="flex-1">
          <h1 className={`${styles.title} pt6 db dn-md`}>
            {h1 || name}
            <span className="db">
              <PartnerLogo logo={logo} />
            </span>
          </h1>

          <div className="mt8 df aic font-i dn-md">
            <div className="fs18">{getAddress(address)}</div>
            <span className="fs24 pos-r ph1" style={{top: -8}} aria-hidden>
              .
            </span>
            <a href="#directions" className="darkerBlue fs18 font-isb">
              {i18n.t("View Map")}
            </a>
          </div>

          <div className="fs18 font-i df aic mt2 dn-md">
            <a href={`tel:${phoneNumber}`} className="darkerBlue font-isb">
              {formatPhoneNumber(phoneNumber)}
            </a>
          </div>

          <div className="df fdc">
            {banners
              .filter(b => !isFullPageBanner(b))
              // @ts-expect-error TS2345: Argument of type '(b: LocationInfoBlurb, i: number) => Element' is not assignable to parameter of type '(value: Banner, index: number, array: Banner[]) => Element'.
              .map((b: LocationInfoBlurb, i) => (
                <div key={i} className={`${b.bg || ""} ${styles.rapid}`}>
                  <span className={`cIcon-${b.icon || "inf"} ${styles.rapid_c1}`} aria-hidden />
                  <span className={styles.rapid_c2}>{b.text}</span>
                </div>
              ))}
          </div>

          <div
            className={`${styles.address} brdn-f mt0-f`}
            style={isMd ? {borderTop: "none !important", marginTop: "0px !important"} : {}}
          >
            {banners
              .filter(b => b.logo)
              .map(({logo}) => {
                // @ts-expect-error TS2339, TS2339, TS2339: Property 'width' does not exist on type 'NextImageProps | undefined'.,  Property 'height' does not exist on type 'NextImageProps | undefined'.,  Property 'src' does not exist on type 'NextImageProps | undefined'.
                const {width = 300, height = 60, src} = logo;
                return (
                  <div key={src.toString()} className={styles.brandLogo}>
                    {/* @ts-expect-error TS2322: Type '{ alt: string; width: any; height: any; id?: string | undefined; color?: string | undefined; key?: Key | null | undefined; crossOrigin?: "" | "anonymous" | "use-credentials" | undefined; ... 270 more ...; extension?: ImageExtension | undefined; }' is not assignable to type '{ src: string | StaticImport; width?: string | number | undefined; height?: string | number | undefined; layout?: "fixed" | "fill" | "intrinsic" | "responsive" | undefined; ... 11 more ...; onLoadingComplete?: OnLoadingComplete | undefined; }'. */}
                    <NextImage {...{...logo, width, height}} alt="" />
                  </div>
                );
              })}

            <div id="directions" className="brdt brd-gray100 pt6 mt8 brdn-md mt0-md pt0-md">
              <h2 className="fs24 font-csb mt0-f">{i18n.t("Getting here")}</h2>
              <div className="fs18 font-i mt6 df aic fs14-md fw">
                <span>{getAddress(address)}</span>
                <div className="df aic">
                  <span className="fs24 pos-r ph1" style={{top: -8}} aria-hidden>
                    .
                  </span>
                  {geoLocation && location && (
                    <span>
                      {i18n.t("{{dist}} mi away", {
                        dist: roundedMilesBetweenCoordinates(
                          geoLocation.x,
                          geoLocation.y,
                          location.x,
                          location.y,
                        ),
                      })}
                    </span>
                  )}
                </div>
                <div className="df aic">
                  <span className="fs24 pos-r ph1" style={{top: -8}} aria-hidden>
                    .
                  </span>
                  <a href={directionsLink} className="darkerBlue font-isb">
                    {i18n.t("Get Directions")}
                  </a>
                </div>
              </div>
            </div>

            <a
              className="pos-r mt6 maxw52 w100pvw wa-md db"
              href={directionsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="visually-hidden">
                {i18n.t("Open in Google Maps: {{loc}} Location", {loc: location.name})}
              </span>
              <span
                className={`${styles.infobox} zIndex1 font-ib tac fs18 pos-a l50pc t50pc dn-md`}
                aria-hidden
              >
                {location.name}
              </span>
              <NextImage
                alt=""
                width={mapDimensions[0]}
                height={mapDimensions[1]}
                priority
                quality={85}
                src={`https://maps.googleapis.com/maps/api/staticmap?key=${NEXT_PUBLIC_GOOGLE_API_KEY}&center=${latitude},${longitude}&zoom=18&size=640x${Math.round(
                  (640 * mapDimensions[1]) / mapDimensions[0],
                )}&scale=2&format=jpg&markers=anchor:32,64|icon:${googleStaticMapMarker}|${latitude},${longitude}`}
              />
            </a>
          </div>

          <div className={styles.hours}>
            <h2 className="fs24 font-cm fw5 fs28-f mt0-f">{i18n.t("Hours")}</h2>

            <dl className={styles.hours__hours}>
              {/* @ts-expect-error TS7031, TS7031: Binding element 'sId' implicitly has an 'any' type.,  Binding element 's' implicitly has an 'any' type. */}
              {specialties.toArray().map(([sId, s]) => (
                <React.Fragment key={sId}>
                  <dt className={`${styles.hours__top} mt10`}>
                    <strong
                      className={`${styles.hours__name} font-csb ttu gray400 fs14-f ls12 lh12`}
                    >
                      {i18n.t(s.name)}
                    </strong>
                    <div className={styles.hours__isOpen}>
                      {isLocationOpen(location, sId) ? (
                        <span>{i18n.t("Open now")}</span>
                      ) : (
                        <span className={styles.closed}>{i18n.t("Closed now")}</span>
                      )}
                    </div>
                  </dt>

                  <dd className={`${styles.hours__rows} m0`}>
                    {groupHours(location, sId, i18n.t).map((group, i) => (
                      <div className={styles.hours__row} key={i}>
                        {getHoursText(group)}
                      </div>
                    ))}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </div>

          {isPopup && (
            <>
              <div className={styles.hours}>
                <h2 className={`${styles.hours__title} fs24 font-cm fw5 fs28-f mt0-f`}>
                  {i18n.t("Neighborhood")}
                </h2>
                <ul className={`${styles.l} mt4`}>
                  <li>{i18n.t("Free Parking available on-site")}</li>
                </ul>
              </div>
              <div className={styles.hours}>
                <h2 className={`${styles.hours__title} fs24 font-cm fw5 fs28-f mt0-f`}>
                  {i18n.t("COVID-19")}
                </h2>
                <ul className={`${styles.l} mt4`}>
                  <li>
                    <Link href={v5Pages.coronavirus} className="tdu">
                      {i18n.t("Info")}
                    </Link>
                  </li>
                  <li>{i18n.t("Appointment required")}</li>
                  <li>{i18n.t("Referral not required")}</li>
                  <li>{i18n.t("Testing for all patients")}</li>
                </ul>
              </div>
              <div className={styles.b}>
                <Btn link href="/coronavirus" bg="#e6e8eb" color="#191919">
                  COVID-19
                </Btn>
                <Btn link href="/coronavirus#faq">
                  FAQs
                </Btn>
              </div>
            </>
          )}

          {services && services.length > 0 && (
            <div className={styles.services}>
              <h2 className={`${styles.services__title} fs24 font-cm fw5 fs28-f mt0-f`}>
                {i18n.t("Location Services")}
              </h2>
              <ul className={`${styles.services__services} basic`}>
                {/* @ts-expect-error TS7006: Parameter 's' implicitly has an 'any' type. */}
                {services.map(s => (
                  <li key={s.id} className={styles.services__service}>
                    <span className={`cIcon-${s.icon} ${styles.services__icon}`} aria-hidden />
                    <span className={styles.services__name}>{i18n.t(s.name)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {popularReasons?.length > 0 && (
            <div className={`${styles.commonly} mb-8`}>
              <h2 className={`${styles.commonly__title} fs24 font-cm fw5 fs28-f mt0-f`}>
                {i18n.t("Commonly treated at this location")}
              </h2>
              <ul className={`${styles.commonly__reasons} basic`}>
                {popularReasons.map((r, i) => {
                  const color = reasonColors[i % reasonColors.length];
                  const {bg} = color;
                  const textColor = color.light ? "#19191b" : "#fff";
                  return (
                    <li
                      key={r.id}
                      className={styles.commonly__reason}
                      style={{
                        "--bg": bg,
                        "--textColor": textColor,
                      }}
                    >
                      <span
                        className={`${styles.commonly__icon} cIcon-${
                          r.iconName || "defaultReasonIcon"
                        }`}
                        aria-hidden
                      />
                      <span className={styles.commonly__name}>{i18n.t(r.name)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        {!isBookingDisabled(location.id) && (
          <div className={styles.inner__right} style={{marginTop: widgetTopMargin}}>
            <div
              id="widget"
              className="pt4 md:w-[31.5rem]"
              style={{
                top: shouldWidgetStick ? "calc(10vh)" : 0,
                position: shouldWidgetStick ? "sticky" : "inherit",
              }}
            >
              <div
                className={`flex flex-col justify-between  ${
                  shouldWidgetStick ? "bg-white rounded-xl bs2 p-6" : "lg:px-2"
                }`}
              >
                <CareDiscoverySearch
                  reasons={reasons}
                  locations={locations}
                  defaultResults={defaultResultsQuery.data}
                  defaultResultsLoadingCount={loadingRowCount}
                  dialogTitle={i18n.t("Book an Appointment")}
                  defaultListRowVariant={CareEntryRowVariant.SM}
                  selectedLocation={location}
                  hideRegionSelector
                  hideDefaultListController
                  hideDefaultListGradient
                  phoneNumber={formatPhoneNumber(phoneNumber)}
                  canSendApptStartEvent
                  viewId={viewId}
                  defaultResultsFailed={noResults}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Main;
